import React, { useEffect, Fragment } from "react";
import { styled } from '@mui/material/styles';
import Checkbox from "@mui/material/Checkbox";
import { IconButton } from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import { setAnalysisType } from "../../../actions/filters";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import HtmlTooltip from "./HtmlTooltip";
import InfoIcon from "@mui/icons-material/Info";

import { setStartYear, setEndYear } from "../../../actions/filters";

const PREFIX = 'Analysis';

const classes = {
  root: `${PREFIX}-root`,
  infoIcon: `${PREFIX}-infoIcon`,
  title: `${PREFIX}-title`,
  remove: `${PREFIX}-remove`,
  secondaryOption: `${PREFIX}-secondaryOption`,
  name: `${PREFIX}-name`,
  header: `${PREFIX}-header`,
  bold: `${PREFIX}-bold`,
  inset: `${PREFIX}-inset`,
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  
  [`& .${classes.infoIcon}`]: {
    color: '#0078a9',
    fontSize: '0.9rem',
    margin: '0 0.25rem'
    },

  [`& .${classes.title}`]: {
    color: "#4c4c4c",
    fontSize: "0.9rem",
    margin: "0 0 0.5em 0",
    textTransform: "uppercase",
  },

  [`& .${classes.remove}`]: {
    background: "#999999",
    borderRadius: "50%",
    padding: "0em",
    color: "#fff",
    position: "absolute",
    right: "0.5em",
    top: "0.5em",
    "&:hover": {
      background: "#909090",
      boxShadow:
        "0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.1rem",
    },
  },

  [`& .${classes.secondaryOption}`]: {
    marginLeft: "2em",
  },

  [`& .${classes.name}`]: {
    textTransform: "capitalize",
  },

  [`& .${classes.header}`]: {
    display: "flex",
    textAlign: "center",
    width: "max-content",
    "&:hover": {
      cursor: "pointer",
    },
  },

  [`& .${classes.bold}`]: {
    fontWeight: "bold",
  },

  [`& .${classes.inset}`]: {
    marginLeft: "0rem",
  },

}));


const Analysis = ({
  setAnalysisType,
  analysisType,
  setStartYear,
  setEndYear,
  startYear,
  endYear,
}) => {

  const TooltipText = () => {
    return (
      <Fragment>
        <h3>Analysis Type</h3>
        <p>
          <span className={classes.bold}>Historic</span> - Real events
          that have occurred.
        </p>
        <p>
          <span className={classes.bold}>Scenario</span> - A modeled
          event. Includes both probabilistic and deterministic.
        </p>
        <ul>
          <li>
            {" "}
            <p className={classes.inset}>
              <span className={classes.bold}>Probabilistic</span> -
              Modeled after a return interval (e.g., 100-year flood).
            </p>
          </li>
          <li>
            {" "}
            <p className={classes.inset}>
              <span className={classes.bold}>Deterministic</span> -
              Modeled after a hypothetical event (e.g., San Andreas Fault).
            </p>
          </li>
        </ul>
      </Fragment>
    );
  };

  const handleChange = (event) => {
    const name = event.target.name;
    const checked = event.target.checked;
    if (name === "scenario") {
      setAnalysisType({
        historic: analysisType.historic,
        probabilistic: checked,
        deterministic: checked,
      });
    } else {
      setAnalysisType({
        historic:
          name === "historic" ? !analysisType.historic : analysisType.historic,
        probabilistic:
          name === "probabilistic"
            ? !analysisType.probabilistic
            : analysisType.probabilistic,
        deterministic:
          name === "deterministic"
            ? !analysisType.deterministic
            : analysisType.deterministic,
      });
    }
  };

  const remove = () => {
    setAnalysisType({
      historic: false,
      probabilistic: false,
      deterministic: false,
    });
  };

  useEffect(() => {
    if (analysisType.probabilistic || analysisType.deterministic) {
      setStartYear(null);
      setEndYear(null);
    }
  }, [analysisType]);

  useEffect(() => {
    if (startYear && endYear) {
      setAnalysisType({
        historic: true,
        probabilistic: false,
        deterministic: false,
      });
    }
  }, [startYear, endYear]);

  return (
	
    <Root className={classes.root}
	sx={{
		border: "1px solid #e9e9e9",
	    borderRadius: "5px",
	    maxWidth: "16em",
	    minWidth: "13em",
	    padding: "1em",
	    position: "relative",
	    margin: "1em 0",
	    background: "#fff",
	}}>
      <HtmlTooltip
        disableFocusListener
        disableTouchListener
        title={<TooltipText />}
        placement="top"
      >
        <div className={classes.header}>
          <div className={classes.title}>Analysis Type</div>
          <InfoIcon className={classes.infoIcon} />
        </div>
      </HtmlTooltip>
      <IconButton
        className={classes.remove}
        alt="clear"
        aria-label="clear analysis filter"
        onClick={remove}
      >
        <RemoveIcon />
      </IconButton>
      <div className={classes.primaryOption}>
        <Checkbox
          aria-label="filter historic results"
          checked={analysisType.historic}
          onChange={handleChange}
          name="historic"
        />
        <span className={classes.name}>Historic</span>
      </div>
      <div className={classes.primaryOption}>
        <Checkbox
          aria-label="filter scenario results"
          checked={analysisType.probabilistic || analysisType.deterministic}
          onChange={handleChange}
          name="scenario"
        />
        <span className={classes.name}>Scenario</span>
      </div>
      <div className={classes.secondaryOption}>
        <Checkbox
          aria-label="filter probabilistic results"
          checked={analysisType.probabilistic}
          onChange={handleChange}
          name="probabilistic"
        />
        <span className={classes.name}>Probabilistic</span>
      </div>
      <div className={classes.secondaryOption}>
        <Checkbox
          aria-label="filter deterministic results"
          checked={analysisType.deterministic}
          onChange={handleChange}
          name="deterministic"
        />
        <span className={classes.name}>Deterministic</span>
      </div>
    </Root>
  );
};

Analysis.propTypes = {
  setStartYear: PropTypes.func.isRequired,
  setEndYear: PropTypes.func.isRequired,
  setAnalysisType: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  analysisType: state.filters.analysisType,
  startYear: state.filters.startYear,
  endYear: state.filters.endYear,
});

export default connect(mapStateToProps, {
  setAnalysisType,
  setStartYear,
  setEndYear,
})(Analysis);
